import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { CURVE_SPT, CURVE_FWD, CURVE_NDF } from "./RateCache";
import { RateSubscription } from "../model/RateSubscription";
import { RateSubscriptionEvent, RateSubscriptionEventType } from "../model/RateSubscriptionEvent";
import { IRateSubscriptionSource } from "../IRateSubscriptionSource";
import { RateSubscriptionPrimaryService } from "../impl/RateSubscriptionPrimaryService";
import { RateSubscriptionPromiseService } from "../impl/RateSubscriptionPromiseService";
import { OrderDataLookupService } from "../../lookup/model/impl/OrderDataLookupService";
import { OrderDataPromiseLookupService } from "../../lookup/model/impl/OrderDataPromiseLookupService";

import {
  OrderLifeCycleEventImpl,
  OrderDataImpl,
  moment,
  Tenor,
  RequestTrackingType,
  Product,
  BulkOrderLifeCycleEventImpl,
  XferEventImpl,
} from "shared/cubiq-model-multi-asset";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { CcyPairLookupService } from "shared/cubiq-services/lookup/model/impl/CcyPairLookupService";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { PromiseSet } from "shared/cubiq-services/promise/PromiseSet";
import {
  MONITORED_ORDER_STATES,
  checkMonitoredOrderState,
} from "src/shared/auth-services/active-user/model/VisibilityModel";

@Injectable()
export class OrderLifecycleRateSubscriptionSource implements IRateSubscriptionSource {
  private rateSubscriptionSubject: Subject<RateSubscriptionEvent>;
  private rateSubscriptionMap: Map<string, Set<string>> = new Map();
  protected _primed: PromiseSet<boolean> = new PromiseSet<boolean>();

  constructor(
    private rateSubscriptionService: RateSubscriptionPromiseService,
    private worker: CubiqWorker,
    private orderDataPromiseLookupService: OrderDataPromiseLookupService,
    private orderDataLookupService: OrderDataLookupService,
    private ccyPairLookupService: CcyPairLookupService,
  ) {
    (this.rateSubscriptionService as RateSubscriptionPrimaryService).registerRateSubscriptionSource(
      this,
    );
    this.initializeOrders();
  }

  protected initializeOrders(): void {
    this.orderDataPromiseLookupService.isPrimed().then(() => {
      let orders: Array<OrderDataImpl> = this.orderDataLookupService.getAll();

      let rateSubscriptionEvents: RateSubscriptionEvent[] = [];
      let rateSubscriptionEventKeys: string[] = [];

      orders.forEach((order: OrderDataImpl) =>
        this.createSubscriptionEvents(order).forEach((event: RateSubscriptionEvent) =>
          rateSubscriptionEvents.push(event),
        ),
      );
      let rateSubscriptions: RateSubscription[] = rateSubscriptionEvents
        .map((event: RateSubscriptionEvent) => event.rateSubscription)
        .filter((rateSubscription: RateSubscription) => {
          let subscription = new RateSubscription(
            rateSubscription.assetCode,
            rateSubscription.product,
            rateSubscription.tenor,
            rateSubscription.valueDate,
          );
          let found: boolean = rateSubscriptionEventKeys.includes(subscription.key);

          if (!found) {
            rateSubscriptionEventKeys.push(subscription.key);
          }

          return !found;
        })
        .filter(
          (rateSubscription: RateSubscription) =>
            !(this.rateSubscriptionService as RateSubscriptionPrimaryService).isSubscribed(
              rateSubscription,
            ),
        );

      let promises: Promise<any>[] = rateSubscriptions.map((subscription: RateSubscription) =>
        this.rateSubscriptionService.subscribe(subscription),
      );

      Promise.all(promises).finally(() => {
        this._primed.resolve(true);
      });

      this.worker
        .getEncodableObservable(OrderLifeCycleEventImpl.name)
        .subscribe((event: OrderLifeCycleEventImpl) => this.processLifecycleEvent(event));

      this.worker
        .getEncodableObservable(BulkOrderLifeCycleEventImpl.name)
        .subscribe((event: BulkOrderLifeCycleEventImpl) => this.processBulkLifecycleEvent(event));

      this.worker
        .getEncodableObservable(XferEventImpl.name)
        .subscribe((event: XferEventImpl) => this.processTransferEvent(event));
    });
  }

  public registerRateSubscriptionSourceSubject(subject: Subject<RateSubscriptionEvent>) {
    this.rateSubscriptionSubject = subject;
  }

  protected processTransferEvent(event: XferEventImpl): void {
    try {
      if (event.orderData && event.orderData.length > 0) {
        event.orderData.forEach((order: OrderDataImpl) => {
          if (
            order.orderState != null &&
            checkMonitoredOrderState(order) &&
            order.baseCode != null &&
            order.nonBaseCode != null
          ) {
            this.subscribe(order as OrderDataImpl);
          }
        });
      }
    } catch (error) {
      logger.out.warn("Error processing xfer event for rate subscription purposes. ", error, event);
    }
  }

  protected processLifecycleEvent(event: OrderLifeCycleEventImpl): void {
    try {
      if (event.orderData) {
        let order: OrderDataImpl = event.orderData as OrderDataImpl;
        if (
          order.orderState != null &&
          checkMonitoredOrderState(order) &&
          order.baseCode != null &&
          order.nonBaseCode != null
        ) {
          this.subscribe(event.orderData as OrderDataImpl);
        }
      }
    } catch (error) {
      logger.out.warn(
        "Error processing order lifecycle event for rate subscription purposes. ",
        error,
        event,
      );
    }
  }

  protected processBulkLifecycleEvent(event: BulkOrderLifeCycleEventImpl): void {
    if (Array.isArray(event?.events)) {
      event.events.forEach((event: OrderLifeCycleEventImpl) => this.processLifecycleEvent(event));
    }
  }

  protected subscribe(order: OrderDataImpl) {
    this.createSubscriptionEvents(order).forEach((event: RateSubscriptionEvent) =>
      this.rateSubscriptionSubject.next(event),
    );
  }

  protected createSubscriptionEvents(order: OrderDataImpl): RateSubscriptionEvent[] {
    let events: RateSubscriptionEvent[] = [];

    let ccyPair: CubiqTS.CurrencyPair = this.ccyPairLookupService.getCcyPair(
      order.baseCode,
      order.nonBaseCode,
    );
    let ndfEnabled: boolean = ccyPair != null ? ccyPair.ndfEnabledFg : true;
    let rateSubscriptions: RateSubscription[] =
      OrderLifecycleRateSubscriptionSource.getSubscriptions(order, ndfEnabled);

    rateSubscriptions.forEach((rateSubscription) => {
      let subscriptionMap: Set<string>;
      let orderKey: string = order.orderId.toString() + "";

      if (!this.rateSubscriptionMap.has(rateSubscription.key)) {
        subscriptionMap = new Set();
        this.rateSubscriptionMap.set(rateSubscription.key, subscriptionMap);
      } else {
        subscriptionMap = this.rateSubscriptionMap.get(rateSubscription.key);
      }

      if (!subscriptionMap.has(orderKey)) {
        subscriptionMap.add(orderKey);
      }

      let event: RateSubscriptionEvent = new RateSubscriptionEvent(
        rateSubscription,
        RateSubscriptionEventType.SUBSCRIBE,
      );
      events.push(event);
    });

    return events;
  }

  protected unsubscribe(order: OrderDataImpl) {
    let ccyPair: CubiqTS.CurrencyPair = this.ccyPairLookupService.getCcyPair(
      order.baseCode,
      order.nonBaseCode,
    );
    let ndfEnabled: boolean = ccyPair != null ? ccyPair.ndfEnabledFg : true;
    let rateSubscriptions: RateSubscription[] =
      OrderLifecycleRateSubscriptionSource.getSubscriptions(order, ndfEnabled);

    rateSubscriptions.forEach((rateSubscription) => {
      let subscriptionMap = this.rateSubscriptionMap.get(rateSubscription.key);
      let orderKey: string = order.orderId.toString() + "";

      if (subscriptionMap && subscriptionMap.has(orderKey)) {
        subscriptionMap.delete(orderKey);

        if (subscriptionMap.size == 0) {
          this.rateSubscriptionMap.delete(rateSubscription.key);

          let event: RateSubscriptionEvent = new RateSubscriptionEvent(
            rateSubscription,
            RateSubscriptionEventType.UNSUBSCRIBE,
          );
          this.rateSubscriptionSubject.next(event);
        }
      }
    });
  }

  public static getSubscriptions(order: OrderDataImpl, ndfEnabled: boolean): RateSubscription[] {
    let assetCode = order.baseCode + order.nonBaseCode;
    let tenor: Tenor =
      order.settleTenor != null && order.settleTenor != Tenor.EMPTY ? order.settleTenor : null;
    let valueDate: Date = order.settleDate != null ? moment(order.settleDate).toDate() : null;
    let product: string;
    let spotRequired = false;
    let subscriptions = [];

    switch (order.product) {
      case Product.SPT:
        product = CURVE_SPT;
        break;
      case Product.FWD:
        product = CURVE_FWD;
        spotRequired = true;
        break;
      case Product.NDF:
        product = CURVE_NDF;
        spotRequired = true;
        break;
      case Product.SSP:
        product = CURVE_FWD;
        spotRequired = true;
        break;
      default:
        throw new Error(
          `${this.constructor.name}.getSubscription() unsupported product ${
            Product[order.product]
          }`,
        );
    }

    if (tenor != null) subscriptions.push(new RateSubscription(assetCode, product, tenor, null));
    // if (tenor != NDF_TRACKING_TENOR && ndfEnabled === true)
    //   subscriptions.push(new RateSubscription(assetCode, CURVE_NDF, NDF_TRACKING_TENOR, null));
    if (valueDate != null)
      subscriptions.push(new RateSubscription(assetCode, product, Tenor.EMPTY, valueDate));
    if (spotRequired)
      subscriptions.push(new RateSubscription(assetCode, CURVE_SPT, Tenor.SPOT, null));

    return subscriptions;
  }

  public isPrimed(): Promise<boolean> {
    return this._primed.get();
  }

  public static getRateKeys(order: OrderDataImpl, ndfEnabled: boolean): string[] {
    return OrderLifecycleRateSubscriptionSource.getSubscriptions(order, ndfEnabled).map(
      (subscription) => subscription.key,
    );
  }
}
